import { ButtonScaleViewOpts } from 'src/lib/ui/components/ButtonScaleView';
import { FeatureButton } from 'src/game/components/FeatureButton';
import { now, user } from 'src/StateObserver';
import {
  getEventCompletedMilestones,
  getEventMilestones,
  getOngoingTreasureExpressEvent,
  getTreasureExpressTicketsByAttempts,
  getUnclaimedMilestones,
  isTreasureExpressAvailable,
} from 'src/replicant/treasureExpress/treasureExpress.getters';
import { spineLoader } from '@play-co/timestep-spine';
import { AssetGroup } from '@play-co/timestep-core';
import { animateJumpingPoints } from 'src/game/animation/jumpingPoints';
import { downscale } from 'src/conf/scale';

const assets = {
  ticket: 'assets/ui/treasureExpress/shared/ticket.png',
  claimBg: 'assets/ui/treasureExpress/shared/claim-bg.png',
  spine: {
    loadMethod: spineLoader,
    url: 'assets/ui/treasureExpress/animation/home-button',
  },
};

export class TreasureExpressHomeButton extends FeatureButton {
  constructor(opts: ButtonScaleViewOpts) {
    super({
      width: 144,
      height: 160,
      pressedOffsetY: 5,
      labelPanel: {
        width: downscale(195),
        height: downscale(55),
        font: 'Title',
        offsetX: 1,
        offsetY: 2,
        labelOffsetX: -1,
        background: {
          image: assets.claimBg,
        },
      },
      ...opts,
    });

    this.animationStateMap = {
      main: {
        type: 'spine',
        assetGroup: new AssetGroup(Object.values(assets)),
        url: assets.spine.url,
        scale: 0.33,
        offsetX: 1,
        offsetY: 80,
        sequence: ['homeicon'],
      },
    };

    this.setNotification({
      condition: (user, now) => !!getUnclaimedMilestones(user, now),
    });
  }

  public update() {
    // Hide the button if the feature is not available for the user
    if (!isTreasureExpressAvailable(user(), now())) {
      this.hide();
      return;
    }

    // Always show if there are any unclaimed milestones, event if there is no event ognoing
    const hasUnclaimed = !!getUnclaimedMilestones(user(), now());
    this.updateLabelPanel({ background: { visible: hasUnclaimed } });
    if (hasUnclaimed) {
      this.show();
      this.setAnimationState('main');
      this.setLabelLocaleText('treasureExpress.button.claim');
      return;
    }

    // Hide the button while there is no event ongoing and nothing to claim from past events
    const event = getOngoingTreasureExpressEvent(user(), now());
    if (!event) {
      this.hide();
      return;
    }

    // If user has completed all milestones, hide the button
    const milestones = getEventMilestones(user(), event.id);
    if (getEventCompletedMilestones(user(), event.id) >= milestones.length) {
      this.hide();
      return;
    }

    // If the event is just ongoing without anything to claim, show the button with the timer
    this.show();
    this.setAnimationState('main');
    this.setLabelTime({ duration: event.remainingTime });
  }

  public async playProgressAnimation(puzzleAttempts: number) {
    const event = getOngoingTreasureExpressEvent(user(), now());
    if (!event) {
      return;
    }

    const milestones = getEventMilestones(user(), event.id);
    if (getEventCompletedMilestones(user(), event.id) >= milestones.length) {
      return;
    }

    const tickets = getTreasureExpressTicketsByAttempts(puzzleAttempts);
    await animateJumpingPoints({
      button: this,
      count: tickets,
      iconImage: assets.ticket,
    });
  }
}
